import cx from 'classnames';
import { FunctionComponent, JSX } from 'react';
import styles from './Pagination.module.scss';

type Props = {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
    onPageChange: (page: number) => void;
    ariaLabel?: string;
};

const Pagination: FunctionComponent<Props> = ({ currentPage, totalPages, totalItems, itemsPerPage, onPageChange, ariaLabel = 'Sidnavigering' }) => {
    if (totalPages <= 1) {
        return null;
    }

    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = Math.min(startIndex + itemsPerPage, totalItems);

    const goToPage = (page: number) => {
        onPageChange(Math.max(1, Math.min(page, totalPages)));
    };

    return (
        <nav aria-label={ariaLabel} className={styles.pagination}>
            <div className={styles.info}>
                Visar {startIndex + 1}-{endIndex} av {totalItems}
            </div>
            <ul className={styles.list}>
                <li>
                    <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1} aria-label="Föregående sida" className={styles.button}>
                        ‹ Föregående
                    </button>
                </li>
                {renderPageNumbers(currentPage, totalPages, goToPage)}
                <li>
                    <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages} aria-label="Nästa sida" className={styles.button}>
                        Nästa ›
                    </button>
                </li>
            </ul>
        </nav>
    );
};

function renderPageNumbers(currentPage: number, totalPages: number, goToPage: (page: number) => void) {
    const pageNumbers: JSX.Element[] = [];
    const maxVisiblePages = 7;

    if (totalPages <= maxVisiblePages) {
        // Show all pages if total is small
        for (let i = 1; i <= totalPages; i++) {
            pageNumbers.push(renderPageButton(i, currentPage, goToPage));
        }
    } else {
        // Always show first page
        pageNumbers.push(renderPageButton(1, currentPage, goToPage));

        let startPage = Math.max(2, currentPage - 2);
        let endPage = Math.min(totalPages - 1, currentPage + 2);

        // Adjust range if near start
        if (currentPage <= 3) {
            endPage = Math.min(maxVisiblePages - 1, totalPages - 1);
        }

        // Adjust range if near end
        if (currentPage >= totalPages - 2) {
            startPage = Math.max(2, totalPages - (maxVisiblePages - 2));
        }

        // Add ellipsis after first page if needed
        if (startPage > 2) {
            pageNumbers.push(
                <li key="ellipsis-start" className={styles.ellipsis} aria-hidden="true">
                    …
                </li>,
            );
        }

        // Add visible page range
        for (let i = startPage; i <= endPage; i++) {
            pageNumbers.push(renderPageButton(i, currentPage, goToPage));
        }

        // Add ellipsis before last page if needed
        if (endPage < totalPages - 1) {
            pageNumbers.push(
                <li key="ellipsis-end" className={styles.ellipsis} aria-hidden="true">
                    …
                </li>,
            );
        }

        // Always show last page
        pageNumbers.push(renderPageButton(totalPages, currentPage, goToPage));
    }

    return pageNumbers;
}

function renderPageButton(pageNumber: number, currentPage: number, goToPage: (page: number) => void) {
    const isCurrent = pageNumber === currentPage;
    return (
        <li key={pageNumber}>
            <button
                onClick={() => goToPage(pageNumber)}
                aria-label={isCurrent ? `Sida ${pageNumber}, nuvarande sida` : `Gå till sida ${pageNumber}`}
                aria-current={isCurrent ? 'page' : undefined}
                className={cx(styles.button, {
                    [styles.active]: isCurrent,
                })}
            >
                {pageNumber}
            </button>
        </li>
    );
}

export default Pagination;
